import {
  type AssignmentInput,
  can,
  type Invite,
  MONEY_LEVELS,
  type MoneyLevel,
  type OrgUnit,
  type Role,
  type StaffImportResult,
  type StaffMember,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload, UserPlus } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { AssignmentsEditor } from './AssignmentsEditor';
import { InviteResult } from './InviteResult';

const unitsQuery = { queryKey: ['org', 'units'], queryFn: () => api<OrgUnit[]>('/org/units') };
const staffQuery = { queryKey: ['org', 'staff'], queryFn: () => api<StaffMember[]>('/org/staff') };

const STATUS: Record<StaffMember['status'], string> = {
  active: 'Работает',
  leave: 'В отпуске / декрете',
  dismissed: 'Уволен',
};

/** Уровни дерева: подразделение с отступом по глубине */
function flatten(units: OrgUnit[]): Array<OrgUnit & { depth: number }> {
  const kids = new Map<string | null, OrgUnit[]>();
  for (const u of units) {
    const k = u.parentId && units.some((x) => x.id === u.parentId) ? u.parentId : null;
    kids.set(k, [...(kids.get(k) ?? []), u]);
  }
  const out: Array<OrgUnit & { depth: number }> = [];
  const walk = (p: string | null, depth: number) => {
    for (const u of kids.get(p) ?? []) {
      out.push({ ...u, depth });
      if (depth < 20) walk(u.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

function UnitDialog({
  unit,
  units,
  onClose,
}: {
  unit: OrgUnit | 'new';
  units: OrgUnit[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isNew = unit === 'new';
  const [name, setName] = useState(isNew ? '' : unit.name);
  const [code, setCode] = useState(isNew ? '' : (unit.code ?? ''));
  const [parentId, setParentId] = useState(isNew ? '' : (unit.parentId ?? ''));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () => {
      const body = { name, code: code || null, parentId: parentId || null };
      return isNew ? api('/org/units', { body }) : api(`/org/units/${unit.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org'] });
      toast('Подразделение сохранено', 'success');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors({ _: err.message, ...err.fieldErrors() }),
  });
  const remove = useMutation({
    mutationFn: () => api(`/org/units/${(unit as OrgUnit).id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org'] });
      toast('Подразделение удалено', 'success');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors({ _: err.message }),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новое подразделение' : 'Подразделение'}
      footer={
        <>
          {!isNew ? (
            <Button variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
              Удалить
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            form="unit-form"
            type="submit"
            loading={save.isPending}
            disabled={!name.trim()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <form
        id="unit-form"
        className="flex flex-col gap-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setErrors({});
          save.mutate();
        }}
      >
        {errors._ ? <ErrorBox error={new Error(errors._)} /> : null}
        <Field label="Название" htmlFor="u-name" error={errors.name}>
          <Input id="u-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Код" htmlFor="u-code" hint="Можно оставить пустым">
          <Input id="u-code" value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label="Входит в" htmlFor="u-parent">
          <Select id="u-parent" value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">Верхний уровень</option>
            {flatten(units)
              .filter((u) => isNew || u.id !== unit.id)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {'— '.repeat(u.depth)}
                  {u.name}
                </option>
              ))}
          </Select>
        </Field>
      </form>
    </Dialog>
  );
}

function StaffDialog({
  person,
  units,
  onClose,
}: {
  person: StaffMember | 'new';
  units: OrgUnit[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isNew = person === 'new';
  const p = isNew ? null : person;
  const [f, setF] = useState({
    fullName: p?.fullName ?? '',
    position: p?.position ?? '',
    orgUnitId: p?.orgUnitId ?? '',
    email: p?.email ?? '',
    phone: p?.phone ?? '',
    status: p?.status ?? 'active',
    groupLabel: p?.groupLabel ?? '',
    note: p?.note ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        fullName: f.fullName,
        position: f.position || null,
        orgUnitId: f.orgUnitId || null,
        email: f.email || null,
        phone: f.phone || null,
        status: f.status,
        groupLabel: f.groupLabel || null,
        note: f.note || null,
      };
      return isNew ? api('/org/staff', { body }) : api(`/org/staff/${p!.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org'] });
      toast('Сотрудник сохранён', 'success');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors({ _: err.message, ...err.fieldErrors() }),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новый сотрудник' : 'Карточка сотрудника'}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            form="staff-form"
            type="submit"
            loading={save.isPending}
            disabled={!f.fullName.trim()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <form
        id="staff-form"
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setErrors({});
          save.mutate();
        }}
      >
        {errors._ ? (
          <div className="sm:col-span-2">
            <ErrorBox error={new Error(errors._)} />
          </div>
        ) : null}
        <Field label="ФИО" htmlFor="s-name" error={errors.fullName}>
          <Input id="s-name" value={f.fullName} onChange={set('fullName')} />
        </Field>
        <Field label="Должность" htmlFor="s-pos">
          <Input id="s-pos" value={f.position} onChange={set('position')} />
        </Field>
        <Field label="Подразделение" htmlFor="s-unit">
          <Select id="s-unit" value={f.orgUnitId} onChange={set('orgUnitId')}>
            <option value="">Не указано</option>
            {flatten(units).map((u) => (
              <option key={u.id} value={u.id}>
                {'— '.repeat(u.depth)}
                {u.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Статус" htmlFor="s-status">
          <Select id="s-status" value={f.status} onChange={set('status')}>
            {Object.entries(STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Почта"
          htmlFor="s-email"
          error={errors.email}
          hint="Нужна для приглашения; можно дописать позже"
        >
          <Input id="s-email" type="email" value={f.email} onChange={set('email')} />
        </Field>
        <Field label="Телефон" htmlFor="s-phone">
          <Input id="s-phone" value={f.phone} onChange={set('phone')} />
        </Field>
        <Field label="Группа" htmlFor="s-group" hint="Например, «КУС»: для выборки">
          <Input id="s-group" value={f.groupLabel} onChange={set('groupLabel')} />
        </Field>
        <Field label="Заметка" htmlFor="s-note">
          <Input id="s-note" value={f.note} onChange={set('note')} />
        </Field>
      </form>
    </Dialog>
  );
}

function InviteStaffDialog({ person, onClose }: { person: StaffMember; onClose: () => void }) {
  const qc = useQueryClient();
  const me = useMe();
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: () => api<Role[]>('/admin/roles') });
  const [email, setEmail] = useState(person.email ?? '');
  const [moneyLevel, setMoneyLevel] = useState<MoneyLevel>('none');
  const [assignments, setAssignments] = useState<AssignmentInput[]>([]);
  const [created, setCreated] = useState<Invite | null>(null);
  const [error, setError] = useState('');
  const send = useMutation({
    mutationFn: () =>
      api<Invite>(`/org/staff/${person.id}/invite`, {
        body: { email: email || undefined, moneyLevel, assignments },
      }),
    onSuccess: (inv) => {
      setCreated(inv);
      qc.invalidateQueries({ queryKey: ['org'] });
      qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
    onError: (err) => err instanceof ApiError && setError(err.message),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={created ? 'Приглашение готово' : `Пригласить: ${person.fullName}`}
      description={created ? undefined : 'Доступ выдаётся ролями ниже. Без роли приглашение не создаётся.'}
      width="lg"
      footer={
        created ? (
          <Button variant="primary" onClick={onClose}>
            Готово
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={send.isPending}
              disabled={!email || !assignments.length}
              onClick={() => {
                setError('');
                send.mutate();
              }}
            >
              Создать приглашение
            </Button>
          </>
        )
      }
    >
      {created ? (
        <InviteResult
          email={created.email}
          link={created.link}
          expiresAt={created.expiresAt}
          mail={created.mail}
          message={created.message}
          previewId={created.id}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {error ? <ErrorBox error={new Error(error)} /> : null}
          <Field label="Почта" htmlFor="si-email">
            <Input id="si-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {me.tenant?.isOwner ? (
            <Field label="Доступ к деньгам" htmlFor="si-money">
              <Select
                id="si-money"
                value={moneyLevel}
                onChange={(e) => setMoneyLevel(e.target.value as MoneyLevel)}
              >
                {Object.entries(MONEY_LEVELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field label="Роли">
            {roles.data ? (
              <AssignmentsEditor roles={roles.data} value={assignments} onChange={setAssignments} />
            ) : (
              <Spinner />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}

/** Вставка списка: ФИО;Должность;Подразделение;Почта;Телефон;Группа;Заметка — по строке на человека */
function parseRows(text: string) {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const c = l.split(/[;\t]/).map((x) => x.trim());
      return {
        fullName: c[0] ?? '',
        position: c[1] || null,
        orgUnitName: c[2] || null,
        email: c[3] ? c[3].toLowerCase() : null,
        phone: c[4] || null,
        groupLabel: c[5] || null,
        note: c[6] || null,
      };
    });
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [result, setResult] = useState<StaffImportResult | null>(null);
  const [error, setError] = useState('');
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api<StaffImportResult>('/org/staff/import', { body: { rows: parseRows(text), dryRun } }),
    onSuccess: (r) => {
      setResult(r);
      if (!r.dryRun) {
        qc.invalidateQueries({ queryKey: ['org'] });
        toast(`Добавлено: ${r.created}, обновлено: ${r.updated}`, 'success');
      }
    },
    onError: (err) => err instanceof ApiError && setError(err.message),
  });
  const canWrite = !!result?.dryRun && !result.problems.length;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Загрузить список сотрудников"
      description="По строке на человека, поля через «;» или Tab: ФИО; должность; подразделение (вложенные через « / », например «Компания / Отдел»); почта; телефон; группа; заметка. Приглашения при загрузке не отправляются."
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            variant="secondary"
            loading={run.isPending}
            disabled={!text.trim()}
            onClick={() => (setError(''), run.mutate(true))}
          >
            Проверить
          </Button>
          <Button
            variant="primary"
            loading={run.isPending}
            disabled={!canWrite}
            onClick={() => run.mutate(false)}
          >
            Загрузить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <ErrorBox error={new Error(error)} /> : null}
        <Textarea rows={8} value={text} onChange={(e) => (setText(e.target.value), setResult(null))} />
        {result ? (
          <div className="text-[14px] text-ink">
            {result.dryRun ? 'Проверка: ' : 'Готово: '}новых {result.created}, обновится {result.updated},
            новых подразделений {result.unitsCreated}.
            {result.problems.map((p) => (
              <p key={`${p.row}-${p.message}`} className="text-danger">
                Строка {p.row}: {p.message}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

export function OrgPage() {
  const me = useMe();
  const units = useQuery(unitsQuery);
  const staff = useQuery(staffQuery);
  const [unitDlg, setUnitDlg] = useState<OrgUnit | 'new' | null>(null);
  const [staffDlg, setStaffDlg] = useState<StaffMember | 'new' | null>(null);
  const [inviteDlg, setInviteDlg] = useState<StaffMember | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [q, setQ] = useState('');
  const [unitId, setUnitId] = useState('');
  const canInvite = can(me.access, 'admin.users');

  const tree = useMemo(() => flatten(units.data ?? []), [units.data]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (staff.data ?? []).filter(
      (p) =>
        (!unitId || p.orgUnitId === unitId) &&
        (!s ||
          `${p.fullName} ${p.position ?? ''} ${p.email ?? ''} ${p.groupLabel ?? ''}`
            .toLowerCase()
            .includes(s)),
    );
  }, [staff.data, q, unitId]);

  if (units.isLoading || staff.isLoading) return <Spinner />;
  if (units.error || staff.error) return <ErrorBox error={units.error ?? staff.error} />;

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Подразделения"
        actions={
          <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setUnitDlg('new')}>
            Добавить
          </Button>
        }
      >
        {tree.length ? (
          <ul className="flex flex-col">
            {tree.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => setUnitDlg(u)}
                  className="flex min-h-11 w-full items-center justify-between gap-3 border-b border-line py-2 text-left text-[14px] hover:bg-surface-2"
                  style={{ paddingLeft: u.depth * 16 }}
                >
                  <span className="min-w-0 truncate">
                    {u.code ? <span className="mr-2 text-ink-3">{u.code}</span> : null}
                    {u.name}
                  </span>
                  <Badge>{u.staffCount}</Badge>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="Подразделений пока нет">
            Создайте их или загрузите список сотрудников: подразделения создадутся сами.
          </EmptyState>
        )}
      </Panel>

      <Panel
        title={`Сотрудники (${list.length})`}
        actions={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              icon={<Upload className="size-4" />}
              onClick={() => setImportOpen(true)}
            >
              Загрузить список
            </Button>
            <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setStaffDlg('new')}>
              Добавить
            </Button>
          </div>
        }
      >
        <div className="mb-3 grid gap-2 sm:grid-cols-2">
          <Input
            placeholder="Поиск по ФИО, должности, почте"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Поиск"
          />
          <Select value={unitId} onChange={(e) => setUnitId(e.target.value)} aria-label="Подразделение">
            <option value="">Все подразделения</option>
            {tree.map((u) => (
              <option key={u.id} value={u.id}>
                {'— '.repeat(u.depth)}
                {u.name}
              </option>
            ))}
          </Select>
        </div>
        {list.length ? (
          <ul className="flex flex-col">
            {list.map((p) => (
              <li
                key={p.id}
                className="flex flex-col gap-2 border-b border-line py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <button type="button" onClick={() => setStaffDlg(p)} className="min-h-11 min-w-0 text-left">
                  <div className="truncate text-[14px] font-medium text-ink">{p.fullName}</div>
                  <div className="truncate text-[13px] text-ink-2">
                    {[p.position, p.orgUnitName, p.email ?? 'почта не указана'].filter(Boolean).join(' · ')}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {p.groupLabel ? <Badge>{p.groupLabel}</Badge> : null}
                    {p.status !== 'active' ? <Badge>{STATUS[p.status]}</Badge> : null}
                    {p.membershipId ? (
                      <Badge tone="ink">В портале</Badge>
                    ) : p.invitePending ? (
                      <Badge>Приглашён</Badge>
                    ) : null}
                    {p.roleNames.map((r) => (
                      <Badge key={r}>{r}</Badge>
                    ))}
                  </div>
                </button>
                {canInvite && !p.membershipId && p.status !== 'dismissed' ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<UserPlus className="size-4" />}
                    onClick={() => setInviteDlg(p)}
                  >
                    {p.invitePending ? 'Новое приглашение' : 'Пригласить'}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="Никого не найдено" />
        )}
      </Panel>

      {unitDlg ? (
        <UnitDialog unit={unitDlg} units={units.data ?? []} onClose={() => setUnitDlg(null)} />
      ) : null}
      {staffDlg ? (
        <StaffDialog person={staffDlg} units={units.data ?? []} onClose={() => setStaffDlg(null)} />
      ) : null}
      {inviteDlg ? <InviteStaffDialog person={inviteDlg} onClose={() => setInviteDlg(null)} /> : null}
      {importOpen ? <ImportDialog onClose={() => setImportOpen(false)} /> : null}
    </div>
  );
}
